import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Lock, Eye, EyeOff, Check, ShieldCheck, ArrowLeft, Mail } from 'lucide-react';
import http from '../api/http';

const REMEMBER_KEY = 'zeus_sirh_identifiant';

const BENEFITS = [
  'Recrutez les bons profils, plus vite',
  'Contrats, congés et absences suivis en quelques clics',
  'Une paie fiable, sans ressaisie',
];

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700&family=Figtree:wght@400;500;600&display=swap');

.zs-login { font-family: 'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif; }
.zs-display { font-family: 'Bricolage Grotesque', 'Figtree', system-ui, sans-serif; letter-spacing: -0.025em; }

@keyframes zs-rise {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: none; }
}
.zs-rise { animation: zs-rise 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) both; }

@media (prefers-reduced-motion: reduce) {
  .zs-rise { animation: none; }
}
`;

const inputClass =
  'h-12 w-full rounded-lg border border-slate-300 bg-slate-50 pl-11 pr-4 text-[15px] text-[#14161A] ' +
  'placeholder:text-slate-400 transition-colors focus:border-[#0F1E3D] focus:bg-white focus:outline-none ' +
  'focus:ring-4 focus:ring-[#E3A81C]/30';

const primaryButtonClass =
  'flex h-12 w-full items-center justify-center gap-2.5 rounded-lg bg-[#E3A81C] text-[15px] font-bold ' +
  'text-[#0F1E3D] shadow-[0_12px_24px_-12px_rgba(227,168,28,0.9)] transition-colors hover:bg-[#EDB52E] ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#0F1E3D]/30 ' +
  'disabled:cursor-not-allowed disabled:opacity-70';

// Seul l'identifiant est mémorisé, jamais le mot de passe.
const readRemembered = () => {
  try {
    return localStorage.getItem(REMEMBER_KEY) || '';
  } catch {
    return '';
  }
};

const writeRemembered = (value) => {
  try {
    if (value) {
      localStorage.setItem(REMEMBER_KEY, value);
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  } catch {
    /* stockage indisponible : on ignore */
  }
};

const Spinner = () => (
  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#0F1E3D]/30 border-t-[#0F1E3D]" />
);

const Login = () => {
  const [remembered] = useState(readRemembered);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'reset'
  const [username, setUsername] = useState(remembered);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(remembered !== '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [codeInputs, setCodeInputs] = useState(['', '', '', '', '', '']);
  const [codeValidated, setCodeValidated] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { login, forgotPassword, resetPassword } = useAuth();

  const goTo = (nextView) => {
    setError('');
    setResetSent(false);
    setResetCode('');
    setCodeInputs(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setView(nextView);
  };

  const handleCodeChange = async (index, value) => {
    const newInputs = [...codeInputs];
    // N'accepter que les chiffres
    const numericValue = value.replace(/[^0-9]/g, '');
    newInputs[index] = numericValue.slice(0, 1);
    setCodeInputs(newInputs);

    // Mettre à jour resetCode
    const fullCode = newInputs.join('');
    setResetCode(fullCode);

    // Vérifier le code instantanément si les 6 chiffres sont saisis
    if (fullCode.length === 6 && !codeValidated) {
      await verifyCode(fullCode);
    }

    // Passer automatiquement à la case suivante
    if (numericValue && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodePaste = async (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    const newInputs = pastedData.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setCodeInputs(newInputs);
    setResetCode(pastedData);

    // Vérifier le code instantanément si 6 chiffres sont collés
    if (pastedData.length === 6 && !codeValidated) {
      await verifyCode(pastedData);
    }
  };

  const verifyCode = async (code) => {
    setValidatingCode(true);
    setError('');
    try {
      const response = await http.post('/api/auth/verify-reset-code', { code });
      if (response.data.valid) {
        setCodeValidated(true);
      } else {
        setError('Code invalide ou expiré');
        setCodeValidated(false);
      }
    } catch (error) {
      setError('Code invalide ou expiré');
      setCodeValidated(false);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Passer à la case précédente avec Backspace
    if (e.key === 'Backspace' && !codeInputs[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(username.trim(), password, remember);
    if (result.success) {
      writeRemembered(remember ? username.trim() : '');
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');

    if (typeof forgotPassword !== 'function') {
      setError(
        'La réinitialisation en ligne n\'est pas encore activée. Contactez l\'administrateur de votre filiale.'
      );
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPassword(username.trim());
      if (result && result.success === false) {
        setError(result.error || 'Impossible d\'envoyer le code pour le moment. Réessayez dans quelques instants.');
      } else {
        setResetSent(true);
      }
    } catch {
      setError('Impossible d\'envoyer le code pour le moment. Réessayez dans quelques instants.');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      const result = await resetPassword(resetCode, newPassword);
      if (result && result.success === false) {
        setError(result.error || 'Impossible de réinitialiser le mot de passe. Le code est peut-être expiré.');
      } else {
        goTo('login');
        setUsername('');
        setPassword('');
      }
    } catch {
      setError('Impossible de réinitialiser le mot de passe. Réessayez dans quelques instants.');
    }
    setLoading(false);
  };

  const title =
    view === 'login' ? 'Bienvenue sur ZEUS SIRH' : resetSent ? 'Email envoyé' : 'Mot de passe oublié';

  const subtitle =
    view === 'login'
      ? 'Connectez-vous à votre espace'
      : resetSent
      ? 'Vérifiez votre boîte de réception'
      : 'Saisissez votre identifiant, nous vous envoyons un code pour réinitialiser votre mot de passe';

  return (
    <div className="zs-login min-h-screen bg-[#EEF1F7] lg:flex">
      <style>{styles}</style>

      {/* Panneau gauche : photo, bord doré incliné */}
      <aside className="relative z-10 hidden lg:block lg:w-[58%] lg:-mr-[7%]">
        <div
          className="absolute inset-0 bg-[#E3A81C]"
          style={{ clipPath: 'polygon(0 0, 100% 0, 88% 100%, 0 100%)' }}
        />
        <div
          className="absolute inset-0 overflow-hidden bg-[#0F1E3D]"
          style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, calc(88% - 10px) 100%, 0 100%)' }}
        >
          <img
            src="/bkg.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(115deg, rgba(10,22,49,0.94) 0%, rgba(10,22,49,0.86) 45%, rgba(10,22,49,0.45) 100%)',
            }}
          />
        </div>

        <div className="relative z-10 flex min-h-screen flex-col justify-center py-16 pl-14 pr-[17%] xl:pl-20">
          <h1
            className="zs-display zs-rise text-[2.75rem] font-bold leading-[1.04] text-white xl:text-[3.9rem]"
            style={{ animationDelay: '0.1s' }}
          >
            Fini les tableurs.
            <span className="block text-[#E3A81C]">Place aux talents.</span>
          </h1>

          <p
            className="zs-rise mt-7 max-w-[46ch] text-lg leading-8 text-white/80"
            style={{ animationDelay: '0.25s' }}
          >
            Recrutement, contrats, congés, paie : tout le parcours de vos collaborateurs réuni au
            même endroit. Vous gagnez du temps pour ce qui compte vraiment, vos équipes.
          </p>

          <ul className="mt-10 space-y-4">
            {BENEFITS.map((benefit, i) => (
              <li
                key={benefit}
                className="zs-rise flex items-start gap-3 text-[17px] font-medium text-white"
                style={{ animationDelay: `${0.4 + i * 0.12}s` }}
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E3A81C]">
                  <Check className="h-3.5 w-3.5 text-[#0F1E3D]" strokeWidth={3} />
                </span>
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Formulaire */}
      <main className="relative z-0 flex min-h-screen flex-1 flex-col lg:pl-[7%]">
        {/* Bandeau mobile */}
        <div className="bg-[#0F1E3D] px-6 pb-24 pt-12 lg:hidden">
          <h1 className="zs-display text-[2rem] font-bold leading-[1.1] text-white">
            Fini les tableurs.
            <span className="block text-[#E3A81C]">Place aux talents.</span>
          </h1>
        </div>

        <div className="flex flex-1 items-start justify-center px-4 pb-10 lg:items-center lg:px-8 lg:py-12">
          <div className="-mt-14 w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0_30px_70px_-30px_rgba(15,30,61,0.4)] lg:mt-0">
            <div className="flex h-1.5" aria-hidden="true">
              <span className="flex-1 bg-[#E3A81C]" />
              <span className="w-10 bg-[#7A1F2B]" />
            </div>

            <div className="px-7 pb-8 pt-8 sm:px-10 sm:pb-9">
              <div className="flex justify-center">
                <img src="/palla.avif" alt="ZEUS SIRH" className="h-28 w-28 object-contain" />
              </div>

              <h2 className="zs-display mt-5 text-center text-[1.75rem] font-bold leading-tight text-[#0F1E3D]">
                {title}
              </h2>
              <p className="mx-auto mt-1.5 max-w-[34ch] text-center text-[15px] text-slate-500">{subtitle}</p>

              {error && (
                <div
                  role="alert"
                  className="mt-6 rounded-lg border-l-4 border-[#7A1F2B] bg-[#FBEEF0] px-4 py-3 text-sm text-[#7A1F2B]"
                >
                  {error}
                </div>
              )}

              {/* Vue : connexion */}
              {view === 'login' && (
                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label htmlFor="username" className="mb-1.5 block text-sm font-medium text-[#14161A]">
                      Nom d'utilisateur
                    </label>
                    <div className="relative">
                      <User className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className={inputClass}
                        placeholder="Votre identifiant"
                        autoComplete="username"
                        autoFocus={remembered === ''}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-[#14161A]">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${inputClass} pr-12`}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        autoFocus={remembered !== ''}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        aria-pressed={showPassword}
                        className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition-colors hover:text-[#0F1E3D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E3A81C]"
                      >
                        {showPassword ? (
                          <EyeOff className="h-[18px] w-[18px]" />
                        ) : (
                          <Eye className="h-[18px] w-[18px]" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        name="remember"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 accent-[#0F1E3D]"
                      />
                      Se souvenir de moi
                    </label>
                    <button
                      type="button"
                      onClick={() => goTo('forgot')}
                      className="text-sm font-medium text-[#0F1E3D] underline-offset-4 transition-colors hover:text-[#7A1F2B] hover:underline focus-visible:outline-none focus-visible:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>

                  <button type="submit" disabled={loading} aria-busy={loading} className={primaryButtonClass}>
                    {loading ? (
                      <>
                        <Spinner />
                        Connexion en cours
                      </>
                    ) : (
                      'Se connecter'
                    )}
                  </button>
                </form>
              )}

              {/* Vue : demande de réinitialisation */}
              {view === 'forgot' && !resetSent && (
                <form onSubmit={handleForgot} className="mt-8 space-y-5">
                  <div>
                    <label htmlFor="reset-username" className="mb-1.5 block text-sm font-medium text-[#14161A]">
                      Nom d'utilisateur
                    </label>
                    <div className="relative">
                      <User className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        id="reset-username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className={inputClass}
                        placeholder="Votre identifiant"
                        autoComplete="username"
                        autoFocus
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" disabled={loading} aria-busy={loading} className={primaryButtonClass}>
                    {loading ? (
                      <>
                        <Spinner />
                        Envoi en cours
                      </>
                    ) : (
                      'Envoyer le code'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => goTo('login')}
                    className="flex w-full items-center justify-center gap-2 text-sm font-medium text-[#0F1E3D] transition-colors hover:text-[#7A1F2B] focus-visible:outline-none focus-visible:underline"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour à la connexion
                  </button>
                </form>
              )}

              {/* Vue : confirmation d'envoi et réinitialisation */}
              {view === 'forgot' && resetSent && (
                <div className="mt-8">
                  <div className="mb-6 rounded-lg bg-[#FBF1D3] px-4 py-4 text-sm leading-6 text-[#4A3600]">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#B07F0C]" />
                    <p className="mt-1">
                      Si un compte correspond à <strong className="font-semibold">{username.trim()}</strong>, un code de réinitialisation a été envoyé à votre adresse email.
                    </p>
                  </div>

                  <form onSubmit={handleReset} className="space-y-5">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#14161A]">
                        Code de réinitialisation
                      </label>
                      <div className="flex gap-2">
                        {codeInputs.map((value, index) => (
                          <input
                            key={index}
                            id={`code-${index}`}
                            type="text"
                            inputMode="numeric"
                            value={value}
                            onChange={(e) => handleCodeChange(index, e.target.value)}
                            onKeyDown={(e) => handleCodeKeyDown(index, e)}
                            onPaste={handleCodePaste}
                            className={`h-14 w-12 rounded-lg border text-center text-2xl font-bold transition-colors focus:outline-none focus:ring-4 focus:ring-[#E3A81C]/30 ${
                              codeValidated
                                ? 'border-green-500 bg-green-50 text-green-600 focus:border-green-500 focus:bg-white'
                                : 'border-slate-300 bg-slate-50 text-[#0F1E3D] focus:border-[#0F1E3D] focus:bg-white'
                            }`}
                            maxLength={1}
                            required
                          />
                        ))}
                      </div>
                      {validatingCode && (
                        <p className="mt-2 text-sm text-slate-500">Vérification du code...</p>
                      )}
                      {codeValidated && (
                        <p className="mt-2 text-sm text-green-600">Code validé ✓</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-[#14161A]">
                        Nouveau mot de passe
                      </label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                        <input
                          id="new-password"
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={!codeValidated}
                          className={`${inputClass} pr-12 ${!codeValidated ? 'cursor-not-allowed bg-slate-100 text-slate-400' : ''}`}
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          aria-pressed={showPassword}
                          disabled={!codeValidated}
                          className={`absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition-colors hover:text-[#0F1E3D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E3A81C] ${!codeValidated ? 'cursor-not-allowed' : ''}`}
                        >
                          {showPassword ? (
                            <EyeOff className="h-[18px] w-[18px]" />
                          ) : (
                            <Eye className="h-[18px] w-[18px]" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium text-[#14161A]">
                        Confirmer le mot de passe
                      </label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                        <input
                          id="confirm-password"
                          type={showPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={!codeValidated}
                          className={`${inputClass} ${!codeValidated ? 'cursor-not-allowed bg-slate-100 text-slate-400' : ''}`}
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>

                    <button type="submit" disabled={loading || !codeValidated} aria-busy={loading} className={primaryButtonClass}>
                      {loading ? (
                        <>
                          <Spinner />
                          Réinitialisation en cours
                        </>
                      ) : (
                        'Réinitialiser le mot de passe'
                      )}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={() => goTo('login')}
                    className="mt-4 flex w-full items-center justify-center gap-2 text-sm font-medium text-[#0F1E3D] transition-colors hover:text-[#7A1F2B] focus-visible:outline-none focus-visible:underline"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour à la connexion
                  </button>
                </div>
              )}

              <p className="mt-8 flex items-center justify-center gap-2 border-t border-slate-100 pt-5 text-xs text-slate-500">
                <ShieldCheck className="h-4 w-4 shrink-0 text-[#B07F0C]" />
                Connexion sécurisée. Accès réservé aux collaborateurs habilités.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
